import { LICENSE_MODULE } from "@selector/shared";
import { AnimatePresence, motion } from "framer-motion";
import { produce } from "immer";
import { debounce, differenceBy, get, groupBy, intersectionBy } from "lodash-es";
import pluralize from "pluralize";
import { PureComponent, type ReactElement } from "react";
import { connect } from "react-redux";
import type { GroupBase } from "react-select";
import type { FilterOptionOption } from "react-select/dist/declarations/src/filters.d.ts";
import {
  clearSubmitErrors,
  formValueSelector,
  type InjectedFormProps,
  reduxForm,
  stopAsyncValidation,
  SubmissionError,
} from "redux-form";
import { type ActionButtonProps, HStack, InfoToggletip } from "widgets";

import { handleTabInForm, type ISelectOption } from "@/app-utils/form.ts";
import {
  CONSTRAINT_OPERATORS,
  type IRsqlComparisonExpr,
  type IRsqlFilterTree,
  SEARCH_OPERATORS,
} from "@/app-utils/rsql.ts";
import { capitalizeFirstLetter, checkSearchValueTermsLength, pluralizeWord } from "@/app-utils/string.ts";
import {
  type AdvancedSearchParams,
  type Configuration,
  type SampleType,
  type SearchAttribute,
  type SearchResult,
  type SearchScope,
  type SearchType,
} from "@/graphql.ts";
import {
  clearAdvancedSearch,
  clearAdvancedSearchPreview,
  createInitialValues,
  fetchAdvancedSearchPreview,
  getInitialSearchAttributes,
  type IAdvancedSearchState,
  loadAdvancedSearchAttributes,
  loadAdvancedSearchTypes,
  loadSearchScopeOptions,
} from "@/redux/modules/advanced-search.ts";
import { isLicensed } from "@/redux/modules/config.ts";
import { loadSampleTypes } from "@/redux/modules/listing.ts";
import { createAdvancedSearchResultAndRedirect } from "@/redux/modules/search-results.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { CELL_TYPE, ENTITY_TYPE, SAMPLE_FIELDS, SEARCH_RESULTS_LIMIT } from "@/settings/settings.js";

import AdvancedSearchCriteria, {
  AdvancedSearchCriteriaReduxFormField,
  validateFilterOperation,
} from "../forms/AdvancedSearchCriteria.js";
import { Dialog } from "../overlays/Dialogs.js";
import InputField, { InputReduxFormField } from "../redux-form/InputField.js";
import ReactSelectField, { ReactSelectReduxFormField } from "../redux-form/ReactSelectField.js";
import SearchScopeSelector from "../SearchScopeSelector.js";

export const ADVANCED_SEARCH_FORM = "advancedSearch";

// selectedSearchType.entityType -> tooltipMsg
const nameAndDescriptionHelpMessages: Record<string, string> = {
  [ENTITY_TYPE.ORGANISM]:
    "For a genome search, use this field to search genome names, genome descriptions, species names, common names, and strain identifiers",
  [ENTITY_TYPE.GENE]: "For a gene search, use this field to search gene names, synonyms, and descriptions",
  [ENTITY_TYPE.TRANSCRIPT]: "For a transcript search, use this field to search transcript names and descriptions",
  [ENTITY_TYPE.PROTEIN]: "For a protein search, use this field to search protein names, synonyms and descriptions",
  [ENTITY_TYPE.SAMPLE]: "For a sample search, use this field to search sample names or descriptions",
  [ENTITY_TYPE.NUFEATURE]: "For a nucleotide feature search, use this field to search by name, synonym or description",
  [ENTITY_TYPE.AAFEATURE]: "For an amino acid feature search, use this field to search by name, synonym or description",
  [ENTITY_TYPE.MARKER]: "For a marker search, use this field to search by name or description",
};

interface IAdvancedSearchOwnProps {
  onApply: () => void;
  onCancel: () => void;
}

interface IAdvancedSearchAppStateProps {
  advancedSearchState: IAdvancedSearchState;
  sampleTypes: SampleType[];
  isLoadingSampleTypes: boolean;
  selectedSearchType: SearchType; // needed to adjust the list of additional criterias
  selectedRestrictionType: ISelectOption; // needed to adjust the list of scope options
  nameOrDescription: string;
  scopeRestriction: SearchScope[];
  selectedSampleTypes: SampleType[];
  initialValues: Partial<any>;
  configuration: Configuration;
}

interface IAdvancedSearchDispatchProps {
  loadAdvancedSearchTypes: () => Promise<SearchType[]>;
  loadAdvancedSearchAttributes: (searchType: SearchType) => Promise<SearchAttribute[]>;
  clearAdvancedSearch: () => void;
  clearAdvancedSearchPreview: () => void;
  createSearchResult: (
    searchType: string,
    searchSubTypeId: number,
    quickSearchQuery: string,
    filterConstraint: IRsqlFilterTree,
    scopeRestriction: SearchScope[],
  ) => Promise<SearchResult>;
  loadSearchScopeOptions: (searchTerm: string, restrictionType: string) => Promise<SearchScope[]>;
  loadSampleTypes: () => Promise<SampleType[]>;
  clearSubmitErrors: () => void;
}

export interface IAdvancedSearchProps
  extends IAdvancedSearchAppStateProps,
    IAdvancedSearchDispatchProps,
    IAdvancedSearchOwnProps,
    InjectedFormProps<any, any> {}

function mapStateToProps(state: IAppState, _ownProps: IAdvancedSearchOwnProps): IAdvancedSearchAppStateProps {
  const sessionParams = state.auth.session?.params;
  // If the initialParams in the advancedSearchState are pre-populated, use these setting.
  // This is the case if a search is repeated ("edit criteria").
  // Otherwise use the last search type, derived from the auth state.
  const initialParams: AdvancedSearchParams = state.advancedSearch.initialParams
    ? state.advancedSearch.initialParams
    : {
        attributesQueryExpression: null,
        quickSearchQuery: null,
        restrictionType: null,
        restrictions: null,
        searchType: sessionParams?.lastAdvancedSearchType,
        searchSubTypeId:
          sessionParams?.lastAdvancedSearchSubTypeId < 0 ? null : sessionParams?.lastAdvancedSearchSubTypeId,
      };
  const selectedSearchType = formValueSelector(ADVANCED_SEARCH_FORM)(state, AdvancedSearch.FIELD_ADV_SEARCH_TYPE);
  const selectedRestrictionType: ISelectOption = formValueSelector(ADVANCED_SEARCH_FORM)(
    state,
    AdvancedSearch.FIELD_ADV_SEARCH_RESTRICTION_TYPE,
  );
  return {
    advancedSearchState: state.advancedSearch,
    sampleTypes: state.listing.sampleTypes,
    isLoadingSampleTypes: state.listing.isLoadingSampleTypes,
    initialValues: createInitialValues(
      state.advancedSearch.searchTypes,
      state.advancedSearch.searchAttributes,
      initialParams,
      selectedSearchType,
    ),
    selectedSearchType: formValueSelector(ADVANCED_SEARCH_FORM)(state, AdvancedSearch.FIELD_ADV_SEARCH_TYPE),
    nameOrDescription: formValueSelector(ADVANCED_SEARCH_FORM)(
      state,
      AdvancedSearch.FIELD_ADV_SEARCH_NAMEORDESCRIPTION,
    ),
    selectedRestrictionType: selectedRestrictionType /*&& selectedRestrictionType.value*/,
    scopeRestriction: formValueSelector(ADVANCED_SEARCH_FORM)(state, AdvancedSearch.FIELD_ADV_SEARCH_SCOPE),
    selectedSampleTypes: formValueSelector(ADVANCED_SEARCH_FORM)(state, AdvancedSearch.FIELD_ADV_SEARCH_SAMPLE_TYPE),
    configuration: state.config.configuration,
  };
}

function mapDispatchToProps(dispatch): IAdvancedSearchDispatchProps {
  return {
    loadAdvancedSearchTypes: () => dispatch(loadAdvancedSearchTypes()),
    loadAdvancedSearchAttributes: (searchType: SearchType) => dispatch(loadAdvancedSearchAttributes(searchType)),
    loadSearchScopeOptions: (searchTerm: string, restrictionType: string) =>
      dispatch(loadSearchScopeOptions(searchTerm, restrictionType)).catch((error) =>
        dispatch(stopAsyncValidation(ADVANCED_SEARCH_FORM, { _error: error.message })),
      ),
    loadSampleTypes: () => dispatch(loadSampleTypes()),
    clearAdvancedSearch: () => dispatch(clearAdvancedSearch()),
    clearAdvancedSearchPreview: () => dispatch(clearAdvancedSearchPreview()),
    createSearchResult: (
      searchType: string,
      searchSubTypeId: number,
      quickSearchQuery: string,
      filterConstraint: IRsqlFilterTree,
      scopeRestriction: SearchScope[],
    ) =>
      dispatch(
        createAdvancedSearchResultAndRedirect(
          searchType,
          searchSubTypeId,
          quickSearchQuery,
          filterConstraint,
          scopeRestriction,
        ),
      ),
    clearSubmitErrors: () => dispatch(clearSubmitErrors(ADVANCED_SEARCH_FORM)),
  };
}

interface IAdvancedSearchFormState {
  searchFields: SearchAttribute[];
}

class AdvancedSearch extends PureComponent<IAdvancedSearchProps, IAdvancedSearchFormState> {
  public static FIELD_ADV_SEARCH_TYPE = "searchType";
  public static FIELD_ADV_SEARCH_NAMEORDESCRIPTION = "nameOrDescription";
  public static FIELD_ADV_SEARCH_ADD_CRITERIA = "additionalCriteria";
  public static FIELD_ADV_SEARCH_RESTRICTION_TYPE = "restrictionType";
  public static FIELD_ADV_SEARCH_SCOPE = "advancedSearchScope";
  public static FIELD_ADV_SEARCH_SAMPLE_TYPE = "sampleType";
  public static MIN_VALUE_LENGTH = 3;
  public static RESULTS_COUNT_DEBOUNCING = 500;

  public static INITIAL_STATE: IAdvancedSearchFormState = {
    searchFields: [],
  };

  constructor(props: IAdvancedSearchProps) {
    super(props);

    this.state = AdvancedSearch.INITIAL_STATE;

    this.addCriteria = this.addCriteria.bind(this);
    this.removeCriteriaByIndex = this.removeCriteriaByIndex.bind(this);
    this.resetForm = this.resetForm.bind(this);
    this.triggerPreviewResults = this.triggerPreviewResults.bind(this);
    this.renderViewResultsButton = this.renderViewResultsButton.bind(this);
    this.submitForm = this.submitForm.bind(this);
    this.renderAttributeOption = this.renderAttributeOption.bind(this);
    this.debouncedName = this.debouncedName.bind(this);
    this.validateAdvancedSearchCriteria = this.validateAdvancedSearchCriteria.bind(this);

    this.debouncedPreviewResults = debounce(this.triggerPreviewResults, AdvancedSearch.RESULTS_COUNT_DEBOUNCING); // fast debouncing when fields are added/removed
    this.debouncedName = debounce(this.debouncedName, 1000);
  }

  componentDidMount() {
    if (isLicensed(this.props.configuration, LICENSE_MODULE.TRACKER)) {
      this.props.loadSampleTypes();
    }
    this.props
      .loadAdvancedSearchTypes()
      .then((searchTypes) => this.props.loadAdvancedSearchAttributes(this.props.selectedSearchType ?? searchTypes[0]))
      .then((searchAttributes: SearchAttribute[]) =>
        getInitialSearchAttributes(searchAttributes, this.props.advancedSearchState.initialParams),
      )
      .then((searchAttributes: SearchAttribute[]) => {
        this.setState({ searchFields: searchAttributes }, () => {
          // Trigger preview results if the form is mounted from saved criteria to initialize the result count
          if (this.props.advancedSearchState.initialParams) {
            setTimeout(this.triggerPreviewResults, 500); // so that the asyncValdiate function gets the filled props
          }
        });
      });
  }

  componentDidUpdate(prevProps: Readonly<IAdvancedSearchProps>) {
    if (this.props.selectedSearchType && this.props.selectedSearchType !== prevProps.selectedSearchType) {
      this.props.loadAdvancedSearchAttributes(this.props.selectedSearchType);
    }
  }

  addCriteria(newSearchAttribute: SearchAttribute): void {
    if (newSearchAttribute) {
      // reset value and internal state in case if was removed/added again
      this.props.change(newSearchAttribute.field, get(this.props.initialValues, newSearchAttribute.field));
      this.props.untouch(newSearchAttribute.field);

      const searchFields: SearchAttribute[] = produce(this.state.searchFields, (draft) => {
        draft.push(newSearchAttribute);
      });
      this.setState({ searchFields: searchFields }, this.debouncedPreviewResults);
    }
  }

  removeCriteriaByIndex(index: number): void {
    const searchFields: SearchAttribute[] = produce(this.state.searchFields, (draft) => {
      draft.splice(index, 1);
    });
    this.setState({ searchFields: searchFields }, () => setTimeout(this.triggerPreviewResults, 1000));
  }

  removeCriteriaBySearchAttributes = (searchAttributesToRemove: SearchAttribute[]): void => {
    const searchFields: SearchAttribute[] = differenceBy(this.state.searchFields, searchAttributesToRemove, "field");
    this.setState({ searchFields: searchFields }, () => setTimeout(this.debouncedPreviewResults, 1000));
  };

  // when user selects a different search type we should reset the additional fields state
  selectSearchType = (_event, newValue: SearchType, previousValue: SearchType) => {
    if (newValue.entityType !== previousValue.entityType || newValue.subTypeId !== previousValue.subTypeId) {
      this.resetForm();
    }
  };

  selectSampleType = (value: SampleType[]) => {
    // remove search fields that do not belong to the new set of sample types (if any selected)
    if (value && value.length > 0) {
      const searchAttributesToBeRemoved: SearchAttribute[] = this.state.searchFields.filter(
        (attribute: SearchAttribute) =>
          attribute.sampleAttribute && intersectionBy(attribute.sampleAttribute.sampleTypes, value, "id").length === 0,
      );
      this.removeCriteriaBySearchAttributes(searchAttributesToBeRemoved);
    }
  };

  validateAdvancedSearchCriteria(filterOperation: IRsqlComparisonExpr, _values, _props, name: string) {
    if (AdvancedSearch.isTooShortString(filterOperation, this.props.advancedSearchState.searchAttributes)) {
      return `Please enter at least ${AdvancedSearch.MIN_VALUE_LENGTH} characters.`;
    }

    const searchField: SearchAttribute = this.state.searchFields.find(
      (searchAttribute: SearchAttribute) => searchAttribute.field === name,
    );
    if (searchField) {
      return validateFilterOperation(filterOperation, searchField.cellType);
    }
  }

  debouncedName(value) {
    return !value || value.length === 0 || value.length > 2 ? undefined : "Term too short";
  }

  debouncedPreviewResults() {
    this.triggerPreviewResults();
  }

  triggerPreviewResults() {
    this.props.asyncValidate();
  }

  buildNameFieldTitle = (): any => {
    return (
      <HStack>
        Name or description
        <InfoToggletip
          content={
            nameAndDescriptionHelpMessages[
              this.props.selectedSearchType ? this.props.selectedSearchType.entityType : ENTITY_TYPE.ORGANISM
            ]
          }
        />
      </HStack>
    );
  };

  getSearchTypeOptions = () => {
    const advancedSearch: IAdvancedSearchState = this.props.advancedSearchState;
    const searchTypes: SearchType[] = advancedSearch.searchTypes;
    const options: GroupBase<SearchType>[] = [];
    if (searchTypes?.length) {
      const basicTypes = searchTypes.filter((searchType) => searchType.subTypeId == undefined);
      options.push({
        //label: "Classic",
        options: basicTypes,
      } as GroupBase<SearchType>);
      const otherTypes = searchTypes.filter((searchType) => searchType.subTypeId != undefined);
      const groups = groupBy(otherTypes, "label");
      options.push(
        ...Object.keys(groups).map((groupLabel) => {
          return {
            label: capitalizeFirstLetter(pluralize(groupLabel)),
            options: groups[groupLabel],
          } as GroupBase<SearchType>;
        }),
      );
    }
    return options;
  };

  render(): ReactElement {
    const selectedSearchType: SearchType = this.props.selectedSearchType;
    const selectedSampleTypes: SampleType[] = this.props.selectedSampleTypes;
    const advancedSearch: IAdvancedSearchState = this.props.advancedSearchState;

    // if the search is about sample we also take into account the selection of Sample Types to filter the search attributes
    const contextAttributes: SearchAttribute[] =
      selectedSearchType &&
      selectedSearchType.entityType === ENTITY_TYPE.SAMPLE &&
      selectedSampleTypes &&
      selectedSampleTypes.length > 0
        ? advancedSearch.searchAttributes.filter(
            (attribute: SearchAttribute) =>
              !attribute.sampleAttribute ||
              intersectionBy(attribute.sampleAttribute.sampleTypes, selectedSampleTypes, "id").length > 0,
          ) // TODO when SearchAttribute is adapted
        : advancedSearch.searchAttributes;

    const availableAttributes: SearchAttribute[] =
      contextAttributes && differenceBy(contextAttributes, this.state.searchFields, "field");

    return (
      <Dialog
        closeFn={() => this.props.onCancel()}
        mainActionButtonProps={this.renderViewResultsButton()}
        secondaryActionButtonProps={{
          onPress: this.resetForm,
          children: "Clear All",
        }}
        footerMessage={{ message: this.props.error }}
      >
        <div className="advanced-search-dropdown-menu">
          <div
            className={`advanced-search-panel ${this.props.error && "with-error"}`}
            onKeyDownCapture={handleTabInForm}
          >
            <ReactSelectReduxFormField
              name={AdvancedSearch.FIELD_ADV_SEARCH_TYPE}
              getOptionValue={(searchType: SearchType) => `${searchType.entityType}-${searchType.subTypeLabel}`}
              getOptionLabel={(searchType: SearchType) =>
                capitalizeFirstLetter(searchType.subTypeLabel || pluralize(searchType.label))
              }
              label="Search type"
              placeholder="Please select..."
              tooltip="Select the type of search you want to perform"
              options={this.getSearchTypeOptions()}
              component={ReactSelectField}
              isClearable={false}
              isSearchable={true}
              onChange={this.selectSearchType as () => void}
              isLoading={advancedSearch.isConfigLoading}
              menuPortalTarget={null}
              menuPosition={"fixed"}
              customStyles={{
                menuList: (provided) => ({
                  ...provided,
                  maxHeight: "300px",
                }),
              }}
            />

            <InputReduxFormField
              name={AdvancedSearch.FIELD_ADV_SEARCH_NAMEORDESCRIPTION}
              componentClass="input"
              label={this.buildNameFieldTitle()}
              placeholder="Search in name or description"
              component={InputField}
            />

            <AnimatePresence initial={false}>
              {this.props.selectedRestrictionType &&
                selectedSearchType &&
                selectedSearchType.entityType !== ENTITY_TYPE.ORGANISM &&
                selectedSearchType.entityType !== ENTITY_TYPE.SAMPLE && (
                  <motion.div
                    key={"search-scope-sel-field"}
                    initial={{ maxHeight: 0, overflow: "hidden" }}
                    animate={{ maxHeight: 100, overflow: "visible" }}
                    exit={{
                      maxHeight: 0,
                      overflow: "hidden",
                      transition: {
                        maxHeight: { duration: 0.5 },
                      },
                    }}
                    transition={{
                      maxHeight: { duration: 1, ease: "easeOut" },
                    }}
                  >
                    <SearchScopeSelector
                      key={`${AdvancedSearch.FIELD_ADV_SEARCH_SCOPE}-${
                        this.props.selectedSearchType && this.props.selectedSearchType.entityType
                      }`} // in order to force the fetching of restriction options
                      searchType={selectedSearchType.entityType}
                      restrictionType={this.props.selectedRestrictionType}
                      restrictionTypeFieldKey={AdvancedSearch.FIELD_ADV_SEARCH_RESTRICTION_TYPE}
                      scopeFieldKey={AdvancedSearch.FIELD_ADV_SEARCH_SCOPE}
                      loadScopeOptions={this.props.loadSearchScopeOptions}
                      change={this.props.change}
                    />
                  </motion.div>
                )}

              {selectedSearchType && selectedSearchType.entityType === ENTITY_TYPE.SAMPLE && (
                <motion.div
                  key={"sample_type-sel-field"}
                  initial={{ maxHeight: 0, overflow: "hidden" }}
                  animate={{ maxHeight: 100, overflow: "visible" }}
                  exit={{
                    maxHeight: 0,
                    overflow: "hidden",
                    transition: {
                      maxHeight: { duration: 0.5 },
                    },
                  }}
                  transition={{
                    maxHeight: { duration: 1, ease: "easeOut" },
                  }}
                >
                  <ReactSelectReduxFormField
                    name={AdvancedSearch.FIELD_ADV_SEARCH_SAMPLE_TYPE}
                    getOptionLabel={(option: SampleType) => option.name}
                    getOptionValue={(option: SampleType) => option.name}
                    label="Sample type"
                    isMulti={true}
                    tooltip="Select the sample types of search you want to perform"
                    isSearchable
                    placeholder={"Restrict by sample type"}
                    options={this.props.sampleTypes}
                    closeMenuOnSelect
                    openMenuOnFocus
                    component={ReactSelectField}
                    isClearable={true}
                    handleChange={this.selectSampleType}
                    isLoading={this.props.isLoadingSampleTypes}
                    menuPortalTarget={null}
                    menuPosition={"fixed"}
                  />
                </motion.div>
              )}

              {this.state.searchFields.length > 0 && (
                <motion.div
                  key={"separator-field"}
                  initial={{ maxHeight: 0, overflow: "hidden" }}
                  animate={{ maxHeight: 100, overflow: "visible" }}
                  exit={{
                    maxHeight: 0,
                    overflow: "hidden",
                    transition: {
                      maxHeight: { duration: 0.5 },
                    },
                  }}
                  transition={{
                    maxHeight: { duration: 1, ease: "easeOut" },
                  }}
                >
                  <hr />
                </motion.div>
              )}

              {this.state.searchFields.map((searchAttribute: SearchAttribute, index: number) => (
                <motion.div
                  key={`new-adv-search-field-${index}`}
                  initial={{ maxHeight: 0, overflow: "hidden" }}
                  animate={{ maxHeight: 100, overflow: "visible" }}
                  exit={{
                    maxHeight: 0,
                    overflow: "hidden",
                    transition: {
                      maxHeight: { duration: 0.5 },
                    },
                  }}
                  transition={{
                    maxHeight: { duration: 1, ease: "easeOut" },
                  }}
                >
                  <AdvancedSearchCriteriaReduxFormField
                    key={searchAttribute.field}
                    name={searchAttribute.field}
                    searchAttribute={searchAttribute}
                    component={AdvancedSearchCriteria as any}
                    validate={this.validateAdvancedSearchCriteria}
                    handleRemoveCriteria={() => this.removeCriteriaByIndex(index)}
                    immediatelyPreviewResults={this.triggerPreviewResults}
                  />
                </motion.div>
              ))}
            </AnimatePresence>

            <hr />

            <ReactSelectReduxFormField
              name={AdvancedSearch.FIELD_ADV_SEARCH_ADD_CRITERIA}
              placeholder={`Add more ${selectedSearchType ? selectedSearchType.label + " " : ""}criteria`}
              getOptionValue={(option: SearchAttribute) => option.field}
              getOptionLabel={(option: SearchAttribute) => option.name}
              options={availableAttributes}
              formatOptionLabel={this.renderAttributeOption}
              component={ReactSelectField}
              isClearable={false}
              isSearchable={true}
              handleChange={this.addCriteria}
              isLoading={advancedSearch.isConfigLoading || advancedSearch.isAttributesLoading}
              filterOption={(option: FilterOptionOption<SearchAttribute>, filter: string) =>
                AdvancedSearch.getAttributeOptionLabel(option.data).toLowerCase().includes(filter.toLowerCase())
              }
              tooltip="Add search criteria to the form"
              maxMenuHeight={230}
              controlShouldRenderValue={false}
              menuPortalTarget={null}
              menuPosition={"fixed"}
            />
          </div>
        </div>
      </Dialog>
    );
  }

  static getAttributeOptionLabel(attribute: SearchAttribute): string {
    return attribute.groupName ? `${attribute.groupName}: ${attribute.name}` : attribute.name;
  }

  renderAttributeOption = (attribute: SearchAttribute): ReactElement => {
    return <span>{AdvancedSearch.getAttributeOptionLabel(attribute)}</span>;
  };

  formIsEmpty() {
    const quickSearch: string = this.props.nameOrDescription;
    const scopeRestriction: SearchScope[] = this.props.scopeRestriction;
    const sampleTypes: SampleType[] = this.props.selectedSampleTypes;
    return (
      this.state.searchFields.length === 0 &&
      (!quickSearch || !AdvancedSearch.checkSearchValueTerms(quickSearch)) &&
      (!scopeRestriction || scopeRestriction.length === 0) &&
      (!sampleTypes || sampleTypes.length === 0)
    );
  }

  renderViewResultsButton(): ActionButtonProps {
    const advancedSearch: IAdvancedSearchState = this.props.advancedSearchState;
    const searchPreviewCount: number = advancedSearch.searchPreview && advancedSearch.searchPreview.resultSize;
    const isPreviewLoading: boolean = advancedSearch.isPreviewLoading;
    const noResults: boolean = searchPreviewCount === 0;
    const buttonLabel: string = noResults
      ? "No Result"
      : searchPreviewCount
        ? searchPreviewCount > SEARCH_RESULTS_LIMIT
          ? "Too Many Results"
          : `View ${searchPreviewCount} ${pluralizeWord("Result", searchPreviewCount)}`
        : "View Results";
    const tooManyResults: boolean = searchPreviewCount > 0 && searchPreviewCount > SEARCH_RESULTS_LIMIT;

    const entityName: string = (this.props.selectedSearchType && this.props.selectedSearchType.label) || "organism";
    const tooltipText: string = noResults
      ? `Zero ${pluralize(entityName)} match the search criteria you entered. Please revise your search`
      : tooManyResults
        ? `More than ${SEARCH_RESULTS_LIMIT} results. Please refine your search`
        : `View the ${pluralize(entityName)} that match your search criteria`;

    return {
      children: buttonLabel,
      tooltip: tooltipText,
      isLoading: isPreviewLoading || this.props.submitting,
      isDisabled: noResults || tooManyResults,
      onPress: this.props.handleSubmit(this.submitForm),
    };
  }

  resetForm() {
    this.setState(AdvancedSearch.INITIAL_STATE);
    this.props.reset();
    this.props.clearAdvancedSearch();
  }

  submitForm(values: any) {
    const pristine: boolean =
      (this.props.pristine && !this.props.advancedSearchState.initialParams) || this.formIsEmpty();
    if (pristine) {
      throw new SubmissionError({
        _error: "To view search results, first provide some search criteria in the form above",
      });
    }

    const searchType: SearchType = values[AdvancedSearch.FIELD_ADV_SEARCH_TYPE];
    const quickSearch: string = values[AdvancedSearch.FIELD_ADV_SEARCH_NAMEORDESCRIPTION];
    const scopeRestriction: SearchScope[] = values[AdvancedSearch.FIELD_ADV_SEARCH_SCOPE];
    const sampleTypes: SampleType[] = values[AdvancedSearch.FIELD_ADV_SEARCH_SAMPLE_TYPE];

    const filterConstraint: IRsqlFilterTree = AdvancedSearch.getFilterConstraints(
      values,
      this.state.searchFields.map((sf) => sf.field),
      sampleTypes,
      this.props.advancedSearchState.searchAttributes,
    );

    return this.props
      .createSearchResult(searchType.entityType, searchType.subTypeId, quickSearch, filterConstraint, scopeRestriction)
      .then(this.props.onApply)
      .catch((error: Error) => {
        throw new SubmissionError({ _error: error.message });
      });
  }

  static onChange(...params: any[]) {
    const props = params[2]; // because the typescript definition is not up to date !
    if (props.dirty) {
      props.clearSubmitErrors();
      props.asyncValidate(...params);
    }
  }

  static isTooShortString = (filterOperation: IRsqlComparisonExpr, searchAttributes: SearchAttribute[]) => {
    if (!filterOperation) {
      return false;
    }
    const attribute: SearchAttribute = searchAttributes.find((a: SearchAttribute) => a.field === filterOperation.key);
    // the min value length is enforced for
    // - string fields (e.g. Accession)
    // - string_list fields (e.g. GO Ontologies)
    // but not for controlled vocabularies (e.g. Expected Genome, checked by "values")
    return (
      attribute &&
      (attribute.cellType === CELL_TYPE.STRING || attribute.cellType === CELL_TYPE.STRING_LIST) &&
      !attribute.values &&
      filterOperation?.value &&
      (filterOperation.value as string).length < AdvancedSearch.MIN_VALUE_LENGTH
    );
  };

  static getFilterOperations(
    values,
    searchFields: string[],
    searchAttributes: SearchAttribute[],
  ): IRsqlComparisonExpr[] {
    return (
      searchFields
        .map((field: string) => get(values, field) as IRsqlComparisonExpr) // deep get because the redux-form values are built hierarchically if there are dots in the field name
        // we keep only the values of type IRsqlComparisonExpr e.g. get searchType out
        // + we keep only the fields that have all values filled
        .filter(
          (filterOperation: IRsqlComparisonExpr) =>
            filterOperation &&
            filterOperation.operator &&
            filterOperation.value &&
            (filterOperation.value as string).length > 0 &&
            !AdvancedSearch.isTooShortString(filterOperation, searchAttributes) &&
            (filterOperation.operator === SEARCH_OPERATORS.BETWEEN ? filterOperation.value2 : true),
        )
    );
  }

  static getFilterConstraints = (
    values: any[],
    registeredFields: string[],
    sampleTypes: SampleType[],
    searchAttributes: SearchAttribute[],
  ): IRsqlFilterTree => {
    const operations: IRsqlComparisonExpr[] = AdvancedSearch.getFilterOperations(
      values,
      registeredFields,
      searchAttributes,
    );
    // special case for sampleTypes field
    if (sampleTypes && sampleTypes.length > 0) {
      const sampleTypesFilterOperation: IRsqlComparisonExpr = {
        key: SAMPLE_FIELDS.SAMPLE_TYPE,
        operator: SEARCH_OPERATORS.IN,
        value: sampleTypes.map((sampleType: SampleType) => sampleType.name),
      };
      operations.push(sampleTypesFilterOperation);
    }
    // create the rsql expression as a set of AND expressions
    return operations.length > 0 && { [CONSTRAINT_OPERATORS.AND]: operations };
  };

  static async previewResults(...params: any[]) {
    const values = params[0]; // because the typescript definition is not up to date !
    const dispatch = params[1]; // because the typescript definition is not up to date !
    const props = params[2]; // because the typescript definition is not up to date !
    // const blurredFields = params[3];  // because the typescript definition is not up to date !

    const searchType: SearchType = values[AdvancedSearch.FIELD_ADV_SEARCH_TYPE];
    const quickSearch: string = values[AdvancedSearch.FIELD_ADV_SEARCH_NAMEORDESCRIPTION];
    const scopeRestriction: SearchScope[] = values[AdvancedSearch.FIELD_ADV_SEARCH_SCOPE];
    const sampleTypes: SampleType[] = values[AdvancedSearch.FIELD_ADV_SEARCH_SAMPLE_TYPE];

    const filterConstraint: IRsqlFilterTree =
      props.registeredFields &&
      AdvancedSearch.getFilterConstraints(
        values,
        Object.keys(props.registeredFields),
        sampleTypes,
        props.advancedSearchState.searchAttributes,
      );

    if (
      (filterConstraint ||
        quickSearch?.length ||
        (scopeRestriction && scopeRestriction.length > 0) ||
        (sampleTypes && sampleTypes.length > 0)) &&
      !(quickSearch && !AdvancedSearch.checkSearchValueTerms(quickSearch))
    ) {
      try {
        await dispatch(
          fetchAdvancedSearchPreview(
            searchType.entityType,
            searchType.subTypeId,
            quickSearch,
            filterConstraint,
            scopeRestriction,
          ),
        );
      } catch (e) {
        dispatch(stopAsyncValidation(ADVANCED_SEARCH_FORM, { _error: e.message }));
        throw { _error: e.message };
      }
    } else {
      dispatch(clearAdvancedSearchPreview()); // if there is nothing to search let's clear the previous search

      if (quickSearch) {
        const validQuickSearch = AdvancedSearch.checkSearchValueTerms(quickSearch);

        if (!validQuickSearch) {
          const error = {
            [AdvancedSearch.FIELD_ADV_SEARCH_NAMEORDESCRIPTION]: `Every search term should be at least ${AdvancedSearch.MIN_VALUE_LENGTH} characters`,
          };

          dispatch(stopAsyncValidation(ADVANCED_SEARCH_FORM, error));
          throw error;
        }
      }

      // it would be more elegant to use reduxForm().shouldAsyncValidate for checking if the asyncvalidation needs to be triggered
      // however according to the redux-form API we would not have access to values and props...
      return;
    }
  }

  static checkSearchValueTerms = (value): boolean => {
    return checkSearchValueTermsLength(value, AdvancedSearch.MIN_VALUE_LENGTH);
  };

  static shouldAsyncValidate({ syncValidationPasses, trigger, initialized }) {
    if (!syncValidationPasses) {
      return false;
    }
    switch (trigger) {
      case "blur":
        // blurring
        return true;
      case "submit":
        // submitting, we always async validate if form is initialized even if it is pristine
        return initialized;
      default:
        return false;
    }
  }
}

const AdvancedSearchDialog = connect<
  IAdvancedSearchAppStateProps,
  IAdvancedSearchDispatchProps,
  IAdvancedSearchOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: ADVANCED_SEARCH_FORM,
    onChange: debounce(AdvancedSearch.onChange, AdvancedSearch.RESULTS_COUNT_DEBOUNCING),
    // destroyOnUnmount: false
    shouldAsyncValidate: AdvancedSearch.shouldAsyncValidate,
    asyncValidate: AdvancedSearch.previewResults,
    // it is needed to allow the form to "reinit" since the initValues object depends on the selected search type ...
    enableReinitialize: true,
    // ... and in this case we do not want a full reset behaviour
    keepDirtyOnReinitialize: true,
  })(AdvancedSearch) as any,
);

export { AdvancedSearch, AdvancedSearchDialog };
